import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Users, 
  Search,
  ChevronLeft,
  ChevronRight,
  Phone,
  Calendar,
  FileText,
  Clock,
  Filter,
  RefreshCw,
  User
} from 'lucide-react';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import api, { getLocalizedValue } from '@/services/api';

const DoctorPatients = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;

  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const patientsPerPage = 10;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchPatients();
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = patients.filter(patient => 
        patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.phone?.includes(searchTerm) ||
        patient.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPatients(filtered);
      setCurrentPage(1);
    } else {
      setFilteredPatients(patients);
    }
  }, [searchTerm, patients]);

  const fetchPatients = async () => {
    setLoading(true);
    try {
      // Try doctor-specific patients endpoint first
      let response;
      try {
        response = await api.get('/doctor/patients', {
          params: {
            'with': 'user;medicalHistory',
            'orderBy': 'created_at',
            'sortedBy': 'desc',
            'limit': 100
          }
        });
      } catch (e) {
        // Fallback to general patients endpoint
        response = await api.get('/patients', {
          params: {
            'with': 'user;medicalHistory',
            'orderBy': 'created_at',
            'sortedBy': 'desc',
            'limit': 100
          }
        });
      }
      
      const patientsData = response.data?.data || [];
      
      // Transform API data to our format
      const transformedPatients = patientsData.map(patient => {
        const firstName = getLocalizedValue(patient.first_name, lang) || patient.first_name?.en || '';
        const lastName = getLocalizedValue(patient.last_name, lang) || patient.last_name?.en || '';
        const name = `${firstName} ${lastName}`.trim() || (lang === 'ar' ? 'مريض' : 'Patient');
        
        // Calculate age from birthday
        let age = null;
        if (patient.birthday) {
          const birthDate = new Date(patient.birthday);
          const today = new Date();
          age = today.getFullYear() - birthDate.getFullYear();
        }
        
        // Get conditions from medical history
        const conditions = [];
        if (patient.medicalHistory?.chronic_diseases) {
          conditions.push(...(patient.medicalHistory.chronic_diseases || []));
        }
        
        return {
          id: patient.id,
          name,
          phone: patient.mobile || patient.phone || '',
          email: patient.user?.email || '',
          image: patient.media?.[0]?.url || patient.user?.avatar || null,
          age,
          gender: patient.gender === 'male' 
            ? (lang === 'ar' ? 'ذكر' : 'Male')
            : patient.gender === 'female'
            ? (lang === 'ar' ? 'أنثى' : 'Female')
            : '',
          bloodType: patient.blood_type || patient.medicalHistory?.blood_type || '',
          lastVisit: patient.last_appointment_at ? new Date(patient.last_appointment_at) : null,
          totalVisits: patient.appointments_count || 0,
          conditions,
        };
      });
      
      setPatients(transformedPatients);
      setFilteredPatients(transformedPatients);
      
    } catch (error) {
      console.error('Failed to fetch patients:', error);
      toast.error(lang === 'ar' ? 'فشل في تحميل قائمة المرضى' : 'Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredPatients.length / patientsPerPage);
  const startIndex = (currentPage - 1) * patientsPerPage;
  const currentPatients = filteredPatients.slice(startIndex, startIndex + patientsPerPage);

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="doctor-patients">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/doctor/dashboard')}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {lang === 'ar' ? 'المرضى' : 'Patients'}
                </h1>
                <p className="text-slate-500 dark:text-slate-400">
                  {lang === 'ar' ? `${filteredPatients.length} مريض` : `${filteredPatients.length} patients`}
                </p>
              </div>
            </div>
            <Button variant="outline" size="icon" onClick={fetchPatients} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Search */}
        <Card className="dark:bg-slate-800 border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={lang === 'ar' ? 'البحث عن مريض...' : 'Search patients...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-patients"
              />
            </div>
          </CardContent>
        </Card>

        {/* Patients List */}
        <Card className="dark:bg-slate-800 border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {lang === 'ar' ? 'قائمة المرضى' : 'Patient List'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="flex items-center gap-4 p-4 border dark:border-slate-700 rounded-lg">
                    <Skeleton className="h-12 w-12 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-1/4" />
                      <Skeleton className="h-3 w-1/3" />
                    </div>
                    <Skeleton className="h-8 w-20" />
                  </div>
                ))}
              </div>
            ) : currentPatients.length === 0 ? (
              <div className="text-center py-12">
                <User className="h-12 w-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                <p className="text-slate-500 dark:text-slate-400">
                  {searchTerm 
                    ? (lang === 'ar' ? 'لم يتم العثور على نتائج' : 'No results found')
                    : (lang === 'ar' ? 'لا يوجد مرضى بعد' : 'No patients yet')
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {currentPatients.map(patient => (
                  <div 
                    key={patient.id}
                    className="flex items-center justify-between p-4 border dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/doctor/patients/${patient.id}`)}
                    data-testid={`patient-${patient.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={patient.image} />
                        <AvatarFallback className="bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300">
                          {patient.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h4 className="font-medium text-slate-900 dark:text-white">{patient.name}</h4>
                        <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
                          {patient.age && (
                            <span>{patient.age} {lang === 'ar' ? 'سنة' : 'years'}</span>
                          )}
                          {patient.gender && (
                            <>
                              <span>•</span>
                              <span>{patient.gender}</span>
                            </>
                          )}
                          {patient.bloodType && (
                            <>
                              <span>•</span>
                              <Badge variant="outline" className="h-5 text-xs">
                                {patient.bloodType}
                              </Badge>
                            </>
                          )}
                        </div>
                        {patient.phone && (
                          <p className="text-sm text-slate-400 dark:text-slate-500 flex items-center gap-1 mt-1">
                            <Phone className="h-3 w-3" />
                            {patient.phone}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                        <Calendar className="h-4 w-4" />
                        {patient.lastVisit 
                          ? format(patient.lastVisit, 'PP', { locale })
                          : (lang === 'ar' ? 'لا توجد زيارات' : 'No visits')
                        }
                      </div>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                        {patient.totalVisits} {lang === 'ar' ? 'زيارة' : 'visits'}
                      </p>
                      {patient.conditions.length > 0 && (
                        <div className="flex gap-1 mt-2 justify-end flex-wrap">
                          {patient.conditions.slice(0, 2).map((condition, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {typeof condition === 'object' ? condition.name : condition}
                            </Badge>
                          ))}
                          {patient.conditions.length > 2 && (
                            <Badge variant="secondary" className="text-xs">
                              +{patient.conditions.length - 2}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t dark:border-slate-700">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {lang === 'ar' 
                    ? `عرض ${startIndex + 1}-${Math.min(startIndex + patientsPerPage, filteredPatients.length)} من ${filteredPatients.length}`
                    : `Showing ${startIndex + 1}-${Math.min(startIndex + patientsPerPage, filteredPatients.length)} of ${filteredPatients.length}`
                  }
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    {currentPage} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DoctorPatients;
